import { Badge } from "@vds/badges";
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Button } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Line } from "@vds/lines";
import { Notification } from "@vds/notifications";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { Body, Title } from "@vds/typography";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWindowSize } from "../../../global/hooks/viewportHooks";
import { breakpoints } from "../../../styles/breakpoints";
import { createSearchParams, Link, useLocation, useNavigate } from "react-router-dom";
import { FrameForDataContainer, Image, ImageGrid, StreamDescriptiveText } from "../../../global/components/OnboardingFormStyles/Styles";
import { findImageForList, getFovDetailedStatus } from "../../../global/utility/Utils";
import { fetchLicenses } from "../../../store/actions/license-actions";
import { assignLicense as assignLicenseAction } from "../../../store/actions/license-actions";
import { CVAPPS_LICENSE_TYPE_MESSAGES, CAMERA_ONBOARDING_TEST_IDS, CVAPPS_LICENSE_TYPES, FOVStatus, DEVICE_ACTION } from "../cameraOnboardingConstants";
import "./cvAppsSub.css";
import { TextEllipsis } from "./styles";
import { LICENSE_LABELS,
  LICENSE_VALUES,
  LICENSE_NOTIFICATION_MESSAGES,
  licensesMessage,
  LICENSE_TYPES,
LICENSE_ORIGIN, isV2Sku } from "../../../global/constants/licensesConstants";
import { NOTIFICATION_TYPES, SUBTITLES } from "../../../constants/cameraInfoConstants";
import { isCcdHasInHouseVersion } from "../../../global/utility/devicesUtils";

const CVAppsLicenseType = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { state } = location;
  const FOVState = state?.cameradata?.state || state?.data?.state;
  const cameraData = state?.cameradata || state?.data;
  const cameraId = state?.cameraId;
  const roiList = state?.roiList;
  const cameraName = state?.cameraname;
  const ccdList = useSelector((state) => state.deviceReducer.companionDeviceList);
  const [updatedLicense, setUpdatedLicense] = useState(null);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const navigate = useNavigate();
  const isRegistered = FOVState?.includes("registered");
  const fovList = useSelector((state) => state.deviceReducer.fovList);
  const camInfo = state?.isCameraInfo || state?.camInfo || state?.data;
  const cameraParentCCDInfo = ccdList.find(ccd => ccd?.id === camInfo?.parentdeviceid) ;
  const [nolicenseMessag, setNolicenseMessag] = useState(false);
  const [licenseRadioData, setLicenseRadioData] = useState([
    {
      label: LICENSE_LABELS.NO_LICENSE,
      children: LICENSE_LABELS.SELECT_LICENSE,
      value: LICENSE_VALUES.NO_LICENSE,
      ariaLabel: LICENSE_VALUES.NO_LICENSE,
      disabled: isRegistered
    }
  ]);

  const activeSession = useSelector((state) => state.sessionReducer.activeSession.activeSession);
  const savedStreamImages = useSelector((state) => state.imagesReducer.streamImages);

  const fovListToDisplay2 = fovList.filter((f) => state.FOVId.indexOf(f.id) > -1);
  const fovListToDisplay = fovListToDisplay2.map((fov) => {
    fov.imageUrl = findImageForList(savedStreamImages, fov.id)?.imageUrl;
    return fov;
  });

  const { width: windowWidth } = useWindowSize();
  const buttonSize = windowWidth <= parseInt(breakpoints.tablet) - 1 ? "small" : "medium";
  const licenseListFromReducer = useSelector((state) => state.licenseReducer.licenses) || [];
  const isInHouse = Boolean(isCcdHasInHouseVersion(cameraParentCCDInfo));
  // licenseListFromReducer is already vision-only (filtered at the saga source) —
  // only the v1/v2 version split is needed here.
  const licenseList = (licenseListFromReducer || []).filter(license => {
      if (!license?.sku) return false;
      return isV2Sku(license.sku) === isInHouse;
    })

  useEffect(() => {
    if (licenseList.length === 0) {
      dispatch(fetchLicenses({"accountId":activeSession?.accountId, "origin":LICENSE_ORIGIN.CV_APPS}));
    }
    if (licenseList.length > 0) {
      const licenseRadioDataList = licenseList
        ?.filter((license) => license?.displayName !== LICENSE_TYPES.ENROLLED_APPEARANCE_V2 )
        ?.map((license) => {
          return {
            label: license?.displayName,
            children: `${license?.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`,
            value: license?.sku,
            ariaLabel: license?.sku,
            disabled: isRegistered
          };
        });
      if (licenseRadioData.length == 1) {
        setLicenseRadioData([...licenseRadioData, ...licenseRadioDataList]);
      }
    }
  }, [licenseList, activeSession?.accountId, isRegistered]);

  const handleLicenseSelection = (stream, event) => {
    const licenseType = event.target.value;
    if (licenseType === LICENSE_VALUES.NO_LICENSE) {
      // Reset all license counts to their original values from licenseList
      const resetLicenseRadioData = licenseRadioData.map((k) => {
        if (k.value !== LICENSE_VALUES.NO_LICENSE) {
          const license = licenseList?.find((f) => f?.sku == k?.value);
          if (license) {
            k.children = `${license.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`;
          }
        }
        return k;
      });
      setLicenseRadioData(resetLicenseRadioData);
      setIsNextEnabled(false);
      setNolicenseMessag(false);
    } else if (licenseType !== LICENSE_VALUES.NO_LICENSE && licenseList?.find((f) => f?.sku === licenseType)?.availablelicenses === 0) {
      setIsNextEnabled(false);
      setNolicenseMessag(true);
      const updatedLicenseRadioData = licenseRadioData.map((k) => {
        if (k.value === licenseType) {
          const license = licenseList?.find((f) => f?.sku == k?.value);
          k.children = (
            <span>
              {`${license?.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`}
              <div className="licensemodal-text">
                <Icon name="error" size="small" color="#C44904" />
                <span className="icon-color">{licensesMessage.ZERO_LICENSE_AVAILABLE_MESSAGE}</span>
              </div>
            </span>
          );
        } else if (k.value !== LICENSE_VALUES.NO_LICENSE) {
          const license = licenseList?.find((f) => f?.sku == k?.value);
          k.children = `${license?.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`;
        }
        return k;
      });
      setLicenseRadioData(updatedLicenseRadioData);
    } else {
      const updatedLicenseRadioData = licenseRadioData.map((k) => {
        if (k.value === licenseType) {
          const availableLicenses = parseInt(k.children.split(" ")[0]) - 1;
          k.children = `${availableLicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`;
        } else if (k.value !== LICENSE_VALUES.NO_LICENSE) {
          const license = licenseList?.find((f) => f.sku == k.value);
          k.children = `${license?.availablelicenses} ${LICENSE_LABELS.AVAILABLE_SUFFIX}`;
        }
        return k;
      });
      setLicenseRadioData(updatedLicenseRadioData);
      setIsNextEnabled(true);
      setNolicenseMessag(false);
    }
    setUpdatedLicense(licenseType);
  };

  const cvAppSubButton = async () => {

    const assignPayload = {
      accountname: activeSession?.accountId || "",
      deviceid: Array.isArray(state?.FOVId) ? state?.FOVId[0] : state?.FOVId,
      sku: updatedLicense
    };

    const data = {
      fieldOfViews: state?.fieldOfViews,
      FOVImagesName: state?.FOVImagesName,
      FOVImageURL: state.FOVImageURL,
      FOVNames: state?.FOVNames,
      FOVId: state?.FOVId,
      cameraId: cameraId,
      companionId: state?.companionId,
      siteSelected: state?.siteSelected,
      floorSelected: state?.floorSelected,
      areaSelected: state?.areaSelected,
      subAreaSelected: state?.subAreaSelected,
      cameraname: state?.cameraname,
      action: DEVICE_ACTION.CAMERA_ONBOARDING,
      cameradata: cameraData,
      data: cameraData,
      sku: updatedLicense,
      roiList: roiList,
      camInfo: camInfo,
      licenseSelected: updatedLicense,
      originPage: state?.originPage ? state?.originPage : ""
    };
    dispatch(assignLicenseAction({ ...assignPayload, navigate, stateData: data, fovList }));
  };

  return (
    <>
      <Breadcrumbs surface="light">
        <BreadcrumbItem>
          <Link data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPPS_LICENSE_TYPE_DEVICELINK} to="/devices" className="breadcrums-item_link">
            Cameras
          </Link>
        </BreadcrumbItem>
        {state && state.hasOwnProperty("originPage") && state?.originPage === "cameraDetails" ? (
          <BreadcrumbItem>
            <Link
              data-testid={CAMERA_ONBOARDING_TEST_IDS.CAMERA_INFO_LINK}
              to={{
                pathname: "/cameraInfoPage",
                search: `?${createSearchParams({ cameraId: state?.cameraId })}`
              }}
              state={{
                data: state?.cameradata?.find((item) => item?.id === state?.cameraId) ? state?.cameradata?.find((item) => item?.id === state?.cameraId) : {}
              }}
              className="breadcrums-item_link"
            >
              {state?.cameradata?.find((item) => item?.id === state?.cameraId)?.name}
            </Link>
          </BreadcrumbItem>
        ) : camInfo ? (
          <BreadcrumbItem>
            <Link
              data-testid={CAMERA_ONBOARDING_TEST_IDS.CAMERA_INFO_PAGELINK}
              to={{
                pathname: "/cameraInfoPage",
                search: `?${createSearchParams({ cameraId: state?.cameraId })}`
              }}
              className="breadcrums-item_link"
            >
              {cameraName}
            </Link>
          </BreadcrumbItem>
        ) : (
          <></>
        )}
        <BreadcrumbItem>Licenses</BreadcrumbItem>
      </Breadcrumbs>
      <br></br>
      <div>
        <Title size="medium">{state?.cameraname}</Title>
        <br />
        <FrameForDataContainer className="license-type-frame-container">
          <StreamDescriptiveText className="license-type-intro-text">
           {CVAPPS_LICENSE_TYPE_MESSAGES.STREAM_DESCREPTIVE_TEXT}
           </StreamDescriptiveText>

          {isRegistered && (
            <Notification
              type={NOTIFICATION_TYPES.WARNING}
              title={NOTIFICATION_TYPES.STREAM_CALIBRATION}
              subtitle={SUBTITLES.STREAM_CALIBRATION}
              fullBleed={false}
              inline={false}
              hideCloseButton={true}
              disableFocus={true}
              buttonData={[
                {
                  marginLeft: "450px",
                  children: "Refresh",
                  onClick: (e) => alert(CVAPPS_LICENSE_TYPE_MESSAGES.REFRESH_ALERT),
                 "data-testid": CAMERA_ONBOARDING_TEST_IDS.CVAPPS_LICENSE_TYPE_REFRESH_BUTTON
                }
              ]}
            />
          )}
          <div className="image-container-2">
            {fovListToDisplay?.map((fov, index) => (
              <div>
                <ImageGrid>
                  <div>
                    <Image className="license-type-fov-image" key={index} src={fov?.imageUrl || "./assets/sample.png"} alt="Loading..." />

                    <div className="fovName license-type-fov-name-row" data-full={fov?.name}>
                      <div className="license-type-fov-name-text">
                        <TextEllipsis className="license-type-fov-name-inner">{fov?.name}</TextEllipsis>
                      </div>
                      <div className="license-type-fov-status">
                        {getFovDetailedStatus(fov).status != FOVStatus.ON_BOARDED ? (
                          <Badge className="license-type-fov-status-badge" fillColor={getFovDetailedStatus(fov).color}>{getFovDetailedStatus(fov).status}</Badge>
                        ) : (
                          getFovDetailedStatus(fov).status
                        )}
                      </div>
                    </div>
                  </div>
                </ImageGrid>
                <div className="license-type-radio-group">
                  <RadioButtonGroup
                    onChange={(e) => handleLicenseSelection(fov?.name, e)}
                    data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPPS_LICENSE_TYPE_LICENSE_RADIO_GROUP}
                    error={false}
                    //selectedStream === FOVNames[index].id ||
                    defaultValue={fov?.licenses == undefined ? LICENSE_VALUES.NO_LICENSE : fov?.licenses.length == 0 ? LICENSE_VALUES.NO_LICENSE : fov?.licenses[0]}
                    data={licenseRadioData}
                  />
                </div>
              </div>
            ))}
          </div>
          <Line type="secondary" style={{ backgroundColor: "grey" }} />
          <div className="license-type-next-btn-wrapper">
            {
              <Button size={buttonSize} use="primary" alignment={"right"} disabled={!isNextEnabled}  data-testid={CAMERA_ONBOARDING_TEST_IDS.CVAPPS_LICENSE_TYPE_NEXT_BUTTON}
              onClick={cvAppSubButton}>
                Next
              </Button>
            }
          </div>
        </FrameForDataContainer>
      </div>
    </>
  );
};

export default CVAppsLicenseType;
